"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { TemplateInfo } from "@/lib/claims/eligibility-types";

export interface PrerequisiteSelectorProps {
  /** Currently selected template IDs */
  selectedTemplates: string[];
  /** Callback when template selection changes */
  onSelectionChange: (templateIds: string[]) => void;
  /** Current logic type (AND/OR) */
  logicType: "AND" | "OR";
  /** Callback when logic type changes */
  onLogicTypeChange: (logicType: "AND" | "OR") => void;
  /** Available templates to select from */
  availableTemplates: TemplateInfo[];
  /** Optional className for styling */
  className?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * PrerequisiteSelector Component
 *
 * A multi-select dropdown component for selecting NFT templates as prerequisites,
 * with support for AND/OR logic type selection.
 *
 * Features:
 * - Multi-select dropdown with search functionality
 * - AND/OR logic toggle
 * - Display selected templates with thumbnails
 * - Remove individual templates from selection
 */
export function PrerequisiteSelector({
  selectedTemplates,
  onSelectionChange,
  logicType,
  onLogicTypeChange,
  availableTemplates,
  className,
  disabled = false,
}: PrerequisiteSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter templates based on search term
  const filteredTemplates = availableTemplates.filter(
    (template) =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.templateId.includes(searchTerm)
  );

  // Get template info by ID
  const getTemplateById = (templateId: string): TemplateInfo | undefined => {
    return availableTemplates.find((t) => t.templateId === templateId);
  };

  // Handle template selection toggle
  const handleTemplateToggle = (templateId: string) => {
    if (selectedTemplates.includes(templateId)) {
      onSelectionChange(selectedTemplates.filter((id) => id !== templateId));
    } else {
      onSelectionChange([...selectedTemplates, templateId]);
    }
  };

  // Handle removing a selected template
  const handleRemoveTemplate = (templateId: string) => {
    onSelectionChange(selectedTemplates.filter((id) => id !== templateId));
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-gray-700">
          Required NFTs (Prerequisites)
        </Label>
      </div>

      {/* Logic Type Selector */}
      <div className="flex items-center gap-4">
        <Label htmlFor="logic-type" className="text-sm text-gray-600">
          Logic Type
        </Label>
        <select
          id="logic-type"
          aria-label="Logic Type"
          value={logicType}
          onChange={(e) => onLogicTypeChange(e.target.value as "AND" | "OR")}
          disabled={disabled}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value="AND">AND (All Required)</option>
          <option value="OR">OR (Any One)</option>
        </select>
        <span className="text-xs text-gray-500">
          {logicType === "AND"
            ? "User must own ALL selected NFTs"
            : "User must own at least ONE selected NFT"}
        </span>
      </div>

      {/* Template Selection Dropdown */}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || availableTemplates.length === 0}
          className={cn(
            "flex w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
            "focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
            disabled || availableTemplates.length === 0
              ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          )}
        >
          <span>
            {availableTemplates.length === 0
              ? "No templates available"
              : selectedTemplates.length === 0
              ? "Select templates..."
              : `${selectedTemplates.length} template${selectedTemplates.length === 1 ? "" : "s"} selected`}
          </span>
          <svg
            className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isOpen && availableTemplates.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
            {/* Search Input */}
            <div className="border-b border-gray-200 p-2">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Template List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredTemplates.length === 0 ? (
                <div className="p-3 text-center text-sm text-gray-500">
                  No templates found
                </div>
              ) : (
                filteredTemplates.map((template) => {
                  const isSelected = selectedTemplates.includes(template.templateId);
                  return (
                    <button
                      key={template.templateId}
                      type="button"
                      onClick={() => handleTemplateToggle(template.templateId)}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50",
                        isSelected && "bg-blue-50"
                      )}
                    >
                      {/* Checkbox */}
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded border",
                          isSelected
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        )}
                      >
                        {isSelected && (
                          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>

                      {/* Template Image */}
                      {template.imageUrl ? (
                        <Image
                          src={template.imageUrl}
                          alt={template.name}
                          width={32}
                          height={32}
                          className="rounded object-cover"
                          unoptimized
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-200 text-xs text-gray-400">
                          NFT
                        </div>
                      )}

                      {/* Template Info */}
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium text-gray-900">
                          {template.name}
                        </div>
                        <div className="truncate text-xs text-gray-500">
                          ID: {template.templateId} | {template.collectionName}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected Templates Display */}
      {selectedTemplates.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">Selected Prerequisites:</Label>
          <div className="flex flex-wrap gap-2">
            {selectedTemplates.map((templateId) => {
              const template = getTemplateById(templateId);
              if (!template) return null;

              return (
                <div
                  key={templateId}
                  data-testid="selected-template"
                  className="flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5"
                >
                  {template.imageUrl ? (
                    <Image
                      src={template.imageUrl}
                      alt={template.name}
                      width={20}
                      height={20}
                      className="rounded-full object-cover"
                      unoptimized
                    />
                  ) : (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-200 text-[10px] text-blue-600">
                      NFT
                    </div>
                  )}
                  <span className="text-sm text-blue-800">{template.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTemplate(templateId)}
                    disabled={disabled}
                    aria-label={`Remove ${template.name}`}
                    className="ml-1 text-blue-400 hover:text-blue-600 disabled:opacity-50"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Helper Text */}
      <p className="text-xs text-gray-500">
        Select NFT templates that users must own before they can claim this reward.
        {selectedTemplates.length > 0 && (
          <>
            {" "}
            {logicType === "AND"
              ? `Users must own all ${selectedTemplates.length} selected NFT${selectedTemplates.length === 1 ? "" : "s"}.`
              : `Users must own at least one of the ${selectedTemplates.length} selected NFT${selectedTemplates.length === 1 ? "" : "s"}.`}
          </>
        )}
      </p>
    </div>
  );
}
